import { Config } from '../_config'
import { DomainError } from '../_kernel/DomainError'
import { ErrorsCode } from '../_kernel/ErrorsCode'
import { Fetcher, RequestInitExtended, ResponseFetcher } from './Fetcher'

export type FetchFunction = (url: RequestInfo, init?: RequestInit) => Promise<Response>

export abstract class BaseFetcher implements Fetcher {
  static readonly AUTH_COOKIE_NAME = 'sfl_user'
  private authToken: string | undefined
  private mapper: (url: string) => string = url => url

  constructor(
    private readonly config: Config,
    private readonly token: string | undefined
  ) {}

  get = async <O>(url: string, options: RequestInitExtended = {}): Promise<ResponseFetcher<O>> => {
    const { schemas, ...innerOptions } = options
    return this.request(url, { ...innerOptions, method: 'get' }, schemas)
  }

  del = async <O>(url: string, options: RequestInitExtended = {}): Promise<ResponseFetcher<O>> => {
    const { schemas, ...innerOptions } = options
    return this.request(url, { ...innerOptions, method: 'delete' }, schemas)
  }

  post = async <O>(url: string, options: RequestInitExtended = {}): Promise<ResponseFetcher<O>> => {
    const { schemas, ...innerOptions } = options
    return this.request<O>(url, { ...innerOptions, method: 'post' }, schemas)
  }

  put = async <O>(url: string, options: RequestInitExtended = {}): Promise<ResponseFetcher<O>> => {
    const { schemas, ...innerOptions } = options
    return this.request<O>(url, { ...innerOptions, method: 'put' }, schemas)
  }

  withAuth = (): Fetcher => {
    if (!this.token) throw DomainError.withCodes(ErrorsCode.MISSING_AUTH_TOKEN)

    // The intention of this method is to force it to be declarative
    // when we mark a request as authenticated, that's why we use
    // the property rename, forcing us to execute the method when
    // we want the request to be authenticated:
    this.authToken = this.token

    return this
  }

  withAuthMapper = (mapper: (url: string) => string): Fetcher => {
    try {
      if (this.withAuth()) {
        this.mapper = mapper
      }
    } catch {
      this.mapper = url => url
    }

    return this
  }

  protected async request<O>(
    url: string,
    options?: Omit<RequestInitExtended, 'schemas'>,
    schemas?: RequestInitExtended['schemas']
  ): Promise<ResponseFetcher<O>> {
    try {
      if (url.includes('?') && !schemas?.query) throw DomainError.withCodes(ErrorsCode.MISSING_QUERY_SCHEMA) // prettier-ignore
      if (options?.body && !schemas?.body) throw DomainError.withCodes(ErrorsCode.MISSING_BODY_SCHEMA) // prettier-ignore

      if (url.includes('?')) {
        const queryString = url.split('?')[1]
        const queryParams = Object.fromEntries(new URLSearchParams(queryString).entries())
        schemas?.query?.parse(queryParams)
      }

      if (options?.body) schemas?.body?.parse(JSON.parse(options.body))

      const internalFetcher = this.fetch()
      const resp = await internalFetcher(this.mapper(url), {
        ...options,
        ...(this.authToken && { credentials: 'include' }),
        headers: {
          ...(options?.headers || {}),
          ...{ 'Content-Type': 'application/json; charset=utf-8' },
          ...(this.authToken && { cookie: `sfl_user=${encodeURIComponent(this.authToken)}` }),
        },
      })
      const respCloned = resp.clone()
      if (!resp.ok) throw respCloned

      // https://github.com/node-fetch/node-fetch?tab=readme-ov-file#custom-highwatermark
      const [textContent] = await Promise.all([resp.text(), respCloned.text()])

      const json = Boolean(textContent.length) && JSON.parse(textContent)

      if (json && Object.keys(json).length !== 0 && !schemas?.response) throw DomainError.withCodes(ErrorsCode.MISSING_RESPONSE_SCHEMA) // prettier-ignore
      if (schemas?.response) schemas?.response.parse(json)

      return [undefined, json as O]
    } catch (error) {
      return [error as Error, undefined]
    }
  }

  protected abstract fetch(): FetchFunction
}
